import { When } from 'react-if';
import { Chip, ICON_COLOR } from '@zep-us/zus';
import { SparklingFillIcon } from '@zep-us/zus/icons';
import { useTranslation } from 'next-i18next';
import { type QuizGenerateItem } from '@/api/ai/type';
import { CheckSmallIcon } from '@/icons';
import { QuizCard } from '@/shared/components/QuizCardV2';
import { StaticRichTextRenderer } from '@/shared/features/rich-text';
import { cn } from '@/utils';
import { type AchievementStandard } from '../../../ai-quiz-generation/hooks/types';
import { DifficultyBadge } from './DifficultyBadge';
import { Indicator } from './Indicator';
import { StandardChip } from './TopSlotWithStandard';
const QuizAnswerByType = ({
  item
}: {
  item: QuizGenerateItem;
}) => {
  if (item.type === 'multiple') {
    return <QuizCard.MultipleChoice.Container className="gap-2xs pb-xs">
        {item.options?.map((option, idx) => {
        const isAnswer = item.answer.includes(idx.toString());
        return <QuizCard.MultipleChoice.ItemCompound key={`${idx}-${option}`}>
              <QuizCard.MultipleChoice.ItemCompound.Row>
                <QuizCard.MultipleChoice.ItemCompound.Indicator.Container className={cn(isAnswer ? 'bg-green-500' : 'bg-gray-200')}>
                  <When condition={isAnswer}>
                    <CheckSmallIcon size="lg" fill="white" />
                  </When>
                </QuizCard.MultipleChoice.ItemCompound.Indicator.Container>
                <QuizCard.MultipleChoice.ItemCompound.Content>
                  <StaticRichTextRenderer text={option} className={cn('py-2xs text-heading-sm-bold', isAnswer && 'text-default', !isAnswer && 'text-assistive')} />
                </QuizCard.MultipleChoice.ItemCompound.Content>
              </QuizCard.MultipleChoice.ItemCompound.Row>
            </QuizCard.MultipleChoice.ItemCompound>;
      })}
      </QuizCard.MultipleChoice.Container>;
  }
  if (item.type === 'ox') {
    const value = item.answer[0]?.toLowerCase() === 'o' ? 'o' : 'x';
    return <QuizCard.OXChoice.Container answer={value} className="pb-xs" />;
  }
  if (item.type === 'subjective') {
    return <QuizCard.SubjectiveAnswer.Container className="pb-xs">
        {item.answer?.map((answer, idx) => <QuizCard.SubjectiveAnswer.ItemCompound key={`${idx}-${answer}`}>
            <QuizCard.SubjectiveAnswer.ItemCompound.Marker />
            <QuizCard.SubjectiveAnswer.ItemCompound.Content>
              <StaticRichTextRenderer text={answer} className="text-heading-sm-bold text-default" />
            </QuizCard.SubjectiveAnswer.ItemCompound.Content>
          </QuizCard.SubjectiveAnswer.ItemCompound>)}
      </QuizCard.SubjectiveAnswer.Container>;
  }
  return null;
};
interface BuildAiGeneratedResultItemProps {
  isSelected: boolean;
  onSelect: (isSelected: boolean) => void;
  mappedStandards: AchievementStandard[];
  onDelete?: () => void;
  item: QuizGenerateItem;
  isSaved?: boolean;
}
export const BuildAiGeneratedResultItem = ({
  isSelected,
  onSelect,
  mappedStandards,
  onDelete,
  item,
  isSaved = false
}: BuildAiGeneratedResultItemProps) => {
  const {
    t
  } = useTranslation(['build']);
  return <QuizCard.Root onClick={() => !isSaved && onSelect(!isSelected)} className={cn('shadow-xs cursor-pointer transition-shadow', isSelected && 'outline outline-2 -outline-offset-2 outline-strong', isSaved && 'cursor-default opacity-[0.54]')} data-sentry-element="unknown" data-sentry-component="BuildAiGeneratedResultItem" data-sentry-source-file="BuildAiGeneratedResultItem.tsx">
      <QuizCard.Header className="min-h-[54px] justify-between pt-lg" data-sentry-element="unknown" data-sentry-source-file="BuildAiGeneratedResultItem.tsx">
        <div className="flex items-center gap-2xs">
          <QuizCard.TypeBadge type={item.type} className="border-opacity bg-default text-body-sm-bold" data-sentry-element="unknown" data-sentry-source-file="BuildAiGeneratedResultItem.tsx" />
          <DifficultyBadge difficulty={item.difficulty} className="border border-opacity-assistive bg-neutral" data-sentry-element="DifficultyBadge" data-sentry-source-file="BuildAiGeneratedResultItem.tsx" />
          {mappedStandards.map(standard => <StandardChip key={standard.code} standard={standard} />)}
        </div>
        <Indicator isSaved={isSaved} isSelected={isSelected} onDelete={onDelete} data-sentry-element="Indicator" data-sentry-source-file="BuildAiGeneratedResultItem.tsx" />
      </QuizCard.Header>

      <QuizCard.Content className="gap-xs pb-sm pt-lg" data-sentry-element="unknown" data-sentry-source-file="BuildAiGeneratedResultItem.tsx">
        <StaticRichTextRenderer text={item.question} className="pb-2xs text-heading-md-bold text-default" data-sentry-element="StaticRichTextRenderer" data-sentry-source-file="BuildAiGeneratedResultItem.tsx" />
        <QuizAnswerByType item={item} data-sentry-element="QuizAnswerByType" data-sentry-source-file="BuildAiGeneratedResultItem.tsx" />
      </QuizCard.Content>

      <When condition={!!item.comment} data-sentry-element="When" data-sentry-source-file="BuildAiGeneratedResultItem.tsx">
        <QuizCard.Footer className="items-start justify-start gap-xs bg-neutral px-lg pb-lg pt-md" data-sentry-element="unknown" data-sentry-source-file="BuildAiGeneratedResultItem.tsx">
          <Chip size="md" rounded="full" leftSlot={<SparklingFillIcon size="2xs" fill={ICON_COLOR.neutral} />} className="shrink-0 border border-alternative bg-default text-body-sm-semibold text-neutral" data-sentry-element="Chip" data-sentry-source-file="BuildAiGeneratedResultItem.tsx">
            {t('build:quizCard.footer.explanation')}
          </Chip>
          <StaticRichTextRenderer text={item.comment ?? ''} className="text-body-lg-medium text-neutral" data-sentry-element="StaticRichTextRenderer" data-sentry-source-file="BuildAiGeneratedResultItem.tsx" />
        </QuizCard.Footer>
      </When>
    </QuizCard.Root>;
};